import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import QueryErrorBoundary from '../components/QueryErrorBoundary'
import { useAppSelector } from '../store/store.hooks'
import { useQualityTest, useTestHistory } from './services/QualityControlHooks'

const TestHistoryHeader = ({ testId }: { testId: string }) => {
  const { t } = useTranslation()
  const { qualityTest } = useQualityTest(testId)

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, sm: 8 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {qualityTest.displayName}
            <Typography component="span" variant="h5" sx={{ fontWeight: 400, color: 'text.secondary' }}>
              {' '}
              — {t('qualityControl.history')}
            </Typography>
          </Typography>
          {qualityTest.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {qualityTest.description}
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}
          >
            {qualityTest.category && (
              <Chip
                label={qualityTest.category}
                size="small"
                sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  )
}

const TestHistoryTable = ({ testId }: { testId: string }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Math.max(0, parseInt(searchParams.get('page') ?? '1', 10) - 1)
  const isAdmin = useAppSelector((s) => s.auth.userDetails?.isAdmin ?? false)

  const { history, pageSize } = useTestHistory(testId, page + 1)

  if (history.totalCount === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('qualityControl.noHistory')}</Alert>
      </Box>
    )
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>{t('qualityControl.executionDate')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('qualityControl.observedAt')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('qualityControl.observedBy')}</TableCell>
              {isAdmin && <TableCell sx={{ fontWeight: 700 }}>{t('qualityControl.versionNumber')}</TableCell>}
              <TableCell sx={{ fontWeight: 700 }}>{t('qualityControl.score')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('qualityControl.comments')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {history.items.map((execution) => (
              <TableRow
                key={execution.id}
                hover
                sx={{
                  cursor: 'pointer',
                  ...(execution.isDeleted && { opacity: 0.5, textDecoration: 'line-through' }),
                }}
                onClick={() => navigate(`/quality-control/${testId}/history/${execution.id}`)}
              >
                <TableCell>
                  {new Date(execution.executionDate).toLocaleString(undefined, {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </TableCell>
                <TableCell>
                  {execution.observedAt
                    ? new Date(execution.observedAt).toLocaleString(undefined, {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })
                    : '—'}
                </TableCell>
                <TableCell>{execution.observedBy}</TableCell>
                {isAdmin && <TableCell>{execution.version}</TableCell>}
                <TableCell>
                  <Chip label={execution.score} size="small" variant="outlined" />
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {execution.comment}
                </TableCell>
                <TableCell align="right">
                  <ChevronRightIcon color="action" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={history.totalCount}
        page={page}
        onPageChange={(_, newPage) => setSearchParams({ page: String(newPage + 1) }, { replace: true })}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[pageSize]}
      />
    </>
  )
}

const TestHistoryPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { testId } = useParams<{ testId: string }>()

  if (!testId) {
    return <Navigate to="/quality-control" replace />
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        variant="contained"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/quality-control')}
        sx={{ mb: 2 }}
      >
        {t('qualityControl.backToList')}
      </Button>

      <QueryErrorBoundary>
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress color="secondary" />
            </Box>
          }
        >
          <TestHistoryHeader testId={testId} />
        </Suspense>
      </QueryErrorBoundary>

      <Paper elevation={1} sx={{ borderTop: '4px solid', borderColor: 'primary.main' }}>
        <QueryErrorBoundary>
          <Suspense
            fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress color="secondary" />
              </Box>
            }
          >
            <TestHistoryTable testId={testId} />
          </Suspense>
        </QueryErrorBoundary>
      </Paper>
    </Box>
  )
}

export default TestHistoryPage
